import React, { useContext, useState, useEffect } from 'react'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'
import { HiArrowRight } from 'react-icons/hi'

/**
 * TransferCase — port of Scale's TransferCase. Lets a Case Manager
 * transfer a case from the current mediator to another mediator they
 * manage. Two-step: select mediator → confirm.
 */
export default function TransferCase({ onToast, onTransferred, mediatorOptions = [] }) {
    const { caseData, is_cancled_case } = useContext(CaseDetailContext)
    const [transferDialogIsOpen, setTransferDialogIsOpen] = useState(false)
    const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false)
    const [selected, setSelected] = useState(null)
    const [loading, setLoading] = useState(false)
    // Mediator picker: searchable dropdown replacing the prior <select>.
    const [pickerOpen, setPickerOpen] = useState(false)
    const [pickerQuery, setPickerQuery] = useState('')

    if (!mediatorOptions || mediatorOptions.length === 0) return null

    const openTransferModal = () => {
        setSelected(null)
        setTransferDialogIsOpen(true)
    }

    const onContinue = () => {
        if (!selected) {
            onToast?.({ type: 'warning', message: 'Please select a mediator to transfer the case to.' })
            return
        }
        setTransferDialogIsOpen(false)
        setConfirmDialogIsOpen(true)
    }

    const handleConfirmTransfer = async () => {
        try {
            setLoading(true)
            await api.post(`/cases/${caseData.id}/transfer`, { to_mediator_user_id: selected.value })
            setLoading(false)
            setConfirmDialogIsOpen(false)
            onToast?.({ type: 'success', message: 'Case transferred successfully' })
            if (onTransferred) onTransferred()
            else setTimeout(() => window.location.reload(), 1500)
        } catch (err) {
            setLoading(false)
            const msg = err?.response?.data?.message || err?.message || 'Failed to transfer case'
            onToast?.({ type: 'error', message: msg })
        }
    }

    return (
        <>
            <div className="w-full m-0 p-0 h-full flex items-stretch">
                <button
                    type="button"
                    disabled={is_cancled_case}
                    onClick={openTransferModal}
                    className={`bg-white border border-gray-300 text-gray-600 rounded-md h-11 px-4 py-2 flex justify-between items-center w-full transition-colors ${is_cancled_case ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
                >
                    <span className="text-sm font-medium">Transfer Case</span>
                    <HiArrowRight size={20} className="h-5 w-5 flex-shrink-0 text-gray-500" />
                </button>
            </div>

            {transferDialogIsOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => !loading && setTransferDialogIsOpen(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-[500px] max-w-[95vw] mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b pb-2"><h5 className="text-lg font-semibold mb-1">Transfer Case</h5></div>
                        <div className="mt-4 space-y-4">
                            <p className="text-sm text-gray-600">Select a mediator to transfer this case to:</p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Select Mediator</label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setPickerOpen((v) => !v)}
                                        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    >
                                        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
                                            {selected ? selected.label : 'Select a mediator…'}
                                        </span>
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                                            <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="#0e1e20" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                    {pickerOpen && (() => {
                                        const q = pickerQuery.trim().toLowerCase()
                                        const filtered = q
                                            ? mediatorOptions.filter((o) => (o.label || '').toLowerCase().includes(q))
                                            : mediatorOptions
                                        return (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 flex flex-col" style={{ maxHeight: 320 }}>
                                                <div className="p-2 border-b border-gray-200">
                                                    <div className="relative">
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                                            <path d="M6.417 11.083A4.667 4.667 0 106.417 1.75a4.667 4.667 0 000 9.333zM12.25 12.25l-2.537-2.537" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        <input
                                                            type="text"
                                                            placeholder="Search mediators..."
                                                            value={pickerQuery}
                                                            onChange={(e) => setPickerQuery(e.target.value)}
                                                            autoFocus
                                                            className="w-full h-8 pl-8 pr-2 rounded-md border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="overflow-auto flex-1">
                                                    {filtered.length === 0 && (
                                                        <div className="px-3 py-3 text-sm text-gray-400">No mediators found</div>
                                                    )}
                                                    {filtered.map((opt) => (
                                                        <button
                                                            type="button"
                                                            key={opt.value}
                                                            onClick={() => {
                                                                setSelected(opt)
                                                                setPickerOpen(false)
                                                                setPickerQuery('')
                                                            }}
                                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                                                                selected?.value === opt.value ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'
                                                            }`}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    })()}
                                </div>
                            </div>
                            {selected && (
                                <div className="p-3 bg-blue-50 rounded-md">
                                    <p className="text-sm text-gray-700"><span className="font-semibold">Selected:</span> {selected.label}</p>
                                    {selected.email && <p className="text-xs text-gray-500 mt-1">{selected.email}</p>}
                                </div>
                            )}
                        </div>
                        <div className="w-full flex justify-between gap-3 mt-6">
                            <button type="button" disabled={loading} className="w-full bg-[#EBEBEB] text-black font-semibold rounded-xl py-3" onClick={() => setTransferDialogIsOpen(false)}>Cancel</button>
                            <button type="button" disabled={loading || !selected} onClick={onContinue} className="w-full bg-[#173336] text-white rounded-xl py-3 font-semibold disabled:opacity-60">Transfer</button>
                        </div>
                    </div>
                </div>
            )}

            {confirmDialogIsOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => !loading && setConfirmDialogIsOpen(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-[500px] max-w-[95vw] mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="border-b pb-2"><h5 className="text-lg font-semibold mb-1">Confirm Transfer</h5></div>
                        <div className="mt-4">
                            <p className="text-gray-900 font-semibold text-md">
                                Are you sure you want to transfer this case to <span className="text-blue-600">{selected?.label}</span>?
                            </p>
                            <p className="text-sm text-gray-600 mt-2">
                                This action will transfer the case and notify the selected mediator. The case will be moved to their account.
                            </p>
                        </div>
                        <div className="w-full flex justify-between gap-3 mt-6">
                            <button type="button" disabled={loading} className="w-full bg-[#EBEBEB] text-black font-semibold rounded-xl py-3" onClick={() => { setConfirmDialogIsOpen(false); setTransferDialogIsOpen(true) }}>Cancel</button>
                            <button type="button" disabled={loading} onClick={handleConfirmTransfer} className="w-full bg-[#173336] text-white rounded-xl py-3 font-semibold disabled:opacity-60">
                                {loading ? 'Transferring…' : 'Confirm Transfer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
